import { type IAugmentedJQuery, type IScope, IWindowService } from 'angular';

import template from './mx-shell-nav-tabs-level.html';

import { EventCodes } from '../../../App/model/event-codes';
import { ActionContextType, ActionsContext } from '../../../Shell/Pandora/model/action';

import type { EventService } from '../../../App/services/event-service';
import type { MxNavigationService } from '../../mx-navigation-service';
import type { MxActionManagerService } from '../../../Shell/Pandora/services/mx-action-manager-service';
import { NavigationItem } from '../../model/navigation-item';
import type { DashboardService } from '../../../Dashboard/dashboard.service';

class ShellNavTabsLevelController {
	static readonly $inject = ['$window', '$element', '$scope', 'mx.shell.EventService', 'mx.shell.NavigationService', 'mx.shell.ActionManagerService', 'mx.sb.Dashboard.Service'];
	tabNavItems: NavigationItem[] = [];
	addDashboardSection: { visible: boolean; title: string; button: { title?: string; icon?: string; visible: boolean }; addingInProgress: boolean } = {
		visible: false,
		title: '',
		button: { visible: true },
		addingInProgress: false,
	};
	parentNavItem: NavigationItem | null | undefined;

	constructor(
		private readonly $window: IWindowService,
		private readonly $element: IAugmentedJQuery,
		private readonly $scope: IScope,
		private readonly eventService: EventService,
		private readonly navigationService: MxNavigationService,
		private readonly actionManagerService: MxActionManagerService,
		private readonly dashboardService: DashboardService
	) {
		this.eventService.subscribeTo(EventCodes.CurrentNavigationItemChanged, (_, { navigationItem }) => this.handleNavSwitching(navigationItem), $scope);
		this.eventService.subscribeTo(EventCodes.NavigationItemClicked, (_, { navigationItem }) => this.handleNavSwitching(navigationItem), $scope);
		this.eventService.subscribeTo(EventCodes.LayoutChanged, () => this.setAddDashboardButtonPosition(), $scope);
	}

	$postLink() {
		this.initAria();
	}

	private initAria() {
		this.$element.on('focus', 'md-tabs-canvas', (e: JQueryEventObject) => {
			if (e.target.tagName === 'MD-TABS-CANVAS') {
				setTimeout(() => {
					if (!$(e.relatedTarget).is('.mx-shell-nav-tabs-level__link')) {
						$(this.$element.find('.mx-shell-nav-tabs-level__link[tabindex=0]')[0]).trigger('focus');
					}
				});
			}
		});
	}

	private handleAriaNavItemSwitch() {
		setTimeout(() => {
			this.$element.find('md-tabs-canvas').removeAttr('tabindex');
			this.$element.find('[role=tablist]').attr('role', 'list').removeAttr('tabindex');
			this.$element.find('[role=tab]').attr('role', 'listitem').removeAttr('tabindex');
			this.$element.find('md-tabs-dummy-wrapper [tabindex=0]').removeAttr('tabindex');
			this.$element.find('md-tab-item').removeAttr('tabindex');
			this.$element.find('md-dummy-tab[tabindex]').removeAttr('tabindex');
			const activeElement = this.$window.document.activeElement as HTMLElement;
			if (activeElement?.tagName === 'MD-TAB-ITEM') {
				activeElement.blur();
			}
		});
	}

	async onDashboardNameKeypress(event: KeyboardEvent) {
		if (event.key === 'Escape') {
			this.setAddingMode(false);
			return;
		}

		if (event.key === 'Enter' && this.addDashboardSection.title) {
			await this.addDashboard();
		}
	}

	async setAddDashboardSection() {
		this.addDashboardSection.visible = false;
		this.addDashboardSection.title = '';
		if (!this.tabNavItems?.every((n) => n?.implementationType === 'W')) {
			return;
		}

		const actions = this.actionManagerService.filterActions(await this.actionManagerService.getActions(new ActionsContext([], []), [ActionContextType.None]));
		const addDashboardAction = actions.find((action) => action.name.startsWith('UUX.Client.Dashboard.Add'));

		if (addDashboardAction) {
			this.addDashboardSection.button.title = addDashboardAction?.title;
			this.addDashboardSection.button.icon = addDashboardAction?.icon;
			this.addDashboardSection.visible = true;
		}

		this.setAddDashboardButtonPosition();
	}

	private readonly setAddDashboardButtonPosition = $.debounce(100, () => {
		const btn = this.$element.find('.mx-add-dashboard-section');
		const tabsWidth = this.$element.find('md-pagination-wrapper').width() ?? 0;
		const viewPortWidth = this.$element.width() ?? 0;
		if (viewPortWidth - tabsWidth > 60) {
			btn.css({ 'inset-inline-start': `${tabsWidth + 25}px`, 'inset-inline-end': `auto`, visibility: 'visible', 'margin-top': '-1px' });
		} else {
			btn.css({ 'inset-inline-start': '', 'inset-inline-end': '', visibility: 'visible', 'margin-top': '' });
		}
	}) as () => void;

	async addDashboard() {
		this.eventService.dispatchEvent(EventCodes.TrackTelemetry, {
			eventName: 'Action clicked',
			sender: 'ad537585-236b-c331-cabc-08dd03cb3113',
			senderTitle: 'UUX.Client.Dashboard.Add',
		});

		const title = this.addDashboardSection.title;
		this.setAddingMode(false);
		if (this.parentNavItem?.id && title) {
			const newDashboardNavigationItemId = await this.dashboardService.addDashboard(this.parentNavItem?.id, title);
			await this.navigationService.reload();
			await this.navigationService.setNavigationItemById(newDashboardNavigationItemId);
		}
	}

	setAddingMode(enabled: boolean) {
		this.addDashboardSection.addingInProgress = enabled;
		this.addDashboardSection.title = '';
		this.eventService.dispatchEvent(EventCodes.LayoutChanged);
		if (enabled) {
			setTimeout(() => $('.mx-add-dashboard-section--dashboard-name input').trigger('focus'));
		}
	}

	private readonly handleNavSwitching = $.debounce(100, (navigationItem: NavigationItem) => {
		if (!navigationItem) {
			return;
		}

		this.$scope.$applyAsync(async () => {
			const tabsLevelItem = this.navigationService.getTabsLevelItem(navigationItem);
			this.parentNavItem = tabsLevelItem;
			this.tabNavItems = this.parentNavItem?.children ?? [];
			await this.setAddDashboardSection();
		});

		this.handleAriaNavItemSwitch();
	}) as (navigationItem: NavigationItem) => void;

	async handleItemClick(item: NavigationItem) {
		this.eventService.dispatchEvent(EventCodes.BeforeNavigationItemClickHandler, item);
		this.eventService.dispatchEvent(EventCodes.TrackTelemetry, { eventName: `Navigation clicked`, sender: item.id, senderTitle: item.technicalName });

		let itemToBeOpen = item;
		while (!itemToBeOpen.route && itemToBeOpen.children?.length) {
			itemToBeOpen = itemToBeOpen.children[0];
		}

		await this.navigationService.setCurrentNavigationItem(itemToBeOpen);

		this.eventService.dispatchEvent(EventCodes.NavigationItemClicked, { navigationItem: itemToBeOpen });

		setTimeout(() => {
			$('#DWP-main-content').trigger('focus');
		}, 100);
	}
}

export const mxShellNavTabsLevelComponent = {
	bindings: {},
	controller: ShellNavTabsLevelController,
	controllerAs: 'vm',
	template,
};
